/**
 * Regional consent rule, shared with the marketing site: only visitors in Europe (EU, EEA, UK,
 * Switzerland) must opt in before analytics. Europe is read from the browser time zone. When
 * the zone cannot be read we assume Europe, so the unsure case keeps the stricter rule.
 */

/** Non "Europe/*" zones that are in the EEA or its territories. */
const EUROPEAN_NON_EUROPE_PREFIX_ZONES = new Set([
  "Atlantic/Reykjavik",
  "Atlantic/Canary",
  "Atlantic/Madeira",
  "Atlantic/Azores",
  "Atlantic/Faroe",
  "Arctic/Longyearbyen",
]);

export const readBrowserTimeZone = (): string | null => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.trim() ? zone.trim() : null;
  } catch {
    return null;
  }
};

/** True when the visitor must see a consent choice before analytics: Europe, or unknown. */
export const requiresConsentBanner = (
  timeZone: string | null | undefined = readBrowserTimeZone()
): boolean => {
  if (!timeZone) {
    return true;
  }
  return timeZone.startsWith("Europe/") || EUROPEAN_NON_EUROPE_PREFIX_ZONES.has(timeZone);
};

/** The browser's Global Privacy Control signal (an opt-out of selling or sharing data). */
export const hasGlobalPrivacyControl = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  );
};

export type RegionalConsentDefault = {
  analytics: boolean;
  ads: boolean;
};

/**
 * Consent to assume when the visitor has made no choice. Null means "no default": the
 * visitor stays opted out until they agree, which is today's behavior.
 */
export const getRegionalConsentDefault = (enabled: boolean): RegionalConsentDefault | null => {
  if (!enabled || requiresConsentBanner()) {
    return null;
  }
  return { analytics: true, ads: !hasGlobalPrivacyControl() };
};
